import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CreateUserPreferenceDto, UpdateUserPreferenceDto, UserPreferenceApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { ObjectDiff } from '@thehubz/shared/utils';
import { DetailedDiff } from 'deep-object-diff';
import { Subject, catchError, lastValueFrom, tap } from 'rxjs';
import { ViewType } from '../constants';
import { FilterTab, TableColumn, UserSession } from '../models';
import { GuidedTourConfig } from '../models/global-utility.model';
import { USER_SESSION_TOKEN } from '../user-access/user-session.token';
import { CommonUtilityLib, StorageUtilityLib } from '../util';

export interface Preferences {
  storedFilter?: StoreFilter;
  defaultColumns?: TableColumn[];
  additionalColumns?: TableColumn[];
  sharedTaskStatusColumn?: TableColumn[];
  viewType?: ViewType;
  sort?: Record<string, number>;
}

export type StoreFilter = Record<string, AnyType>;

export interface RefKey {
  _id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  selectedTableColumn = new Subject<{
    defaultColumn: TableColumn[];
    additionalColumn: TableColumn[];
    sharedTaskStatusColumn?: TableColumn[];
    changeType: string;
    resetViewFlag: boolean;
  }>();
  filteredData = new Subject<{ filterResult: AnyType; changeType: string }>();
  userPreference!: AnyType;

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtilityLib: StorageUtilityLib,
    private commonUtilityLib: CommonUtilityLib,
    private userPreferenceApiService: UserPreferenceApiService,
  ) {}

  private getUserId(fail = true) {
    const userSession = this.storageUtilityLib.getLocalStorageNullable<UserSession>(this.userSessionToken);
    if (userSession?.userData?._id) {
      return userSession.userData._id;
    } else if (fail) {
      throw new Error('User not found');
    }
    return undefined;
  }

  async getUserPreferences() {
    this.userPreference = await lastValueFrom(this.userPreferenceApiService.userPreferenceControllerFindOneByUserID());
  }

  createUserPreference(fail = true) {
    const userID = this.getUserId(fail);
    if (userID) {
      this.userPreferenceApiService
        .userPreferenceControllerFindOneByUserID()
        .pipe(
          catchError(async (error: HttpErrorResponse) => {
            if (error.error.message === 'User not found' || error.error === 'User not found') {
              const userPreferences: CreateUserPreferenceDto = { userID };
              this.userPreferenceApiService
                .userPreferenceControllerCreate(userPreferences)
                .pipe(tap((data) => (this.userPreference = data)))
                .subscribe();
            }
          }),
        )
        .subscribe();
    }
  }

  /**
   * set user preferences from localStorage
   * @param columns
   * @param refKey (project | task | asset)
   * @param tabKey (tab unique key)
   */
  setUserPreferences(tabKey: string, refKey: string, columns?: Preferences, filterTabs?: FilterTab[]) {
    this.userPreferenceApiService
      .userPreferenceControllerFindOneByUserID()
      .pipe(
        tap((data) => {
          if (data) {
            const tabKeyObj = !this.commonUtilityLib.isEmptyObject({ ...this.userPreference?.[refKey]?.[tabKey], ...columns })
              ? { ...this.userPreference?.[refKey]?.[tabKey], ...columns }
              : undefined;
            const userPreferences: UpdateUserPreferenceDto = {
              [refKey]: {
                ...this.userPreference?.[refKey],
                filterTabs,
                [tabKey]: tabKeyObj,
              },
            };
            const diffResult = ObjectDiff.detailed(data[refKey as keyof typeof data], userPreferences[refKey as keyof typeof userPreferences]);
            if (!this.isDiffResultEmpty(diffResult))
              this.userPreferenceApiService
                .userPreferenceControllerUpdate(data._id, userPreferences)
                .pipe(tap((data) => (this.userPreference = data)))
                .subscribe();
          }
        }),
      )
      .subscribe();
  }

  isDiffResultEmpty(value: DetailedDiff) {
    for (const propKey in value) {
      if (!this.commonUtilityLib.isEmptyObject(value[propKey as keyof typeof value])) return false;
    }
    return true;
  }

  /**
   * Clear user preferences
   * @param tabKey (tab unique key)
   * @param refKey (project | task)
   */
  clearUserPreferences(tabKey: string, refKey: string, filterKey?: string) {
    switch (filterKey) {
      case 'storedFilter': {
        delete this.userPreference?.[refKey]?.[tabKey]?.['storedFilter'];
        break;
      }
      case 'resetColumns': {
        delete this.userPreference?.[refKey]?.[tabKey]?.['additionalColumns'];
        delete this.userPreference?.[refKey]?.[tabKey]?.['defaultColumns'];
        delete this.userPreference?.[refKey]?.[tabKey]?.['sharedTaskStatusColumn'];
        break;
      }
      case 'viewType': {
        delete this.userPreference?.[refKey]?.[tabKey]?.['viewType'];
        break;
      }
      case 'sort': {
        delete this.userPreference?.[refKey]?.[tabKey]?.['sort'];
        break;
      }
      default: {
        delete this.userPreference?.[refKey]?.[tabKey];
        break;
      }
    }
    if (this.commonUtilityLib.isEmptyObject(this.userPreference?.[refKey]?.[tabKey])) {
      delete this.userPreference?.[refKey]?.[tabKey];
    }
    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((data) => (this.userPreference = data)))
      .subscribe();
  }

  /**
   * Rename tab
   * @param tabKey (tab unique key)
   * @param refKey (project | task)
   * @param previousTabKey
   * @param filterTabs
   */
  renameTab(tabKey: string, refKey: string, previousTabKey: string, filterTabs: FilterTab[]) {
    const previousTabKeyValue = this.userPreference?.[refKey]?.[previousTabKey];
    delete this.userPreference?.[refKey]?.[previousTabKey];
    this.userPreference = {
      ...this.userPreference,
      [refKey]: {
        ...this.userPreference?.[refKey],
        filterTabs,
        [tabKey]: previousTabKeyValue,
      },
    };
    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((data) => (this.userPreference = data)))
      .subscribe();
  }

  /**
   * Get tab (based on key) configuration containing columns and storedFilter info
   * @param tabKey (tab unique key)
   * @param refKey (project | task)
   * @returns
   */
  async tabTableConfig(tabKey: string, refKey: string): Promise<Preferences | undefined> {
    if (!this.userPreference) {
      await this.getUserPreferences();
    }
    return this.userPreference?.[refKey]?.[tabKey];
  }

  /**
   * Get filterTab array stored in the user preference
   * @param refKey (project | task)
   * @returns
   */
  async filterTabConfig(refKey: string): Promise<FilterTab[] | undefined> {
    if (!this.userPreference) {
      await this.getUserPreferences();
    }
    return (this.userPreference?.[refKey] && this.userPreference?.[refKey].filterTabs) || undefined;
  }

  /**
   * Update filter tab with the modified filterTab array
   * @param refKey (project | task)
   * @param filterTabs
   */
  updateFilterTabs(refKey: string, filterTabs?: FilterTab[]) {
    if (this.userPreference) {
      this.userPreference[refKey]['filterTabs'] = filterTabs;
    }
    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((data) => (this.userPreference = data)))
      .subscribe();
  }

  /**
   * set viewType of tabs
   * @param tabKey
   * @param refKey
   * @param viewType
   * @returns
   */
  setViewType(tabKey: string, refKey: string, viewType = 'individual') {
    if (this.userPreference) {
      if (viewType === 'individual') {
        this.clearUserPreferences(tabKey, refKey, 'viewType');
        return;
      } else {
        this.userPreference[refKey][tabKey] = {
          ...this.userPreference[refKey][tabKey],
          ['viewType']: viewType,
        };
      }
    }
    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((data) => (this.userPreference = data)))
      .subscribe();
  }

  setTabSorting(tabKey: string, refKey: string, sort: Record<string, number>) {
    if (this.userPreference) {
      const sortKey = Object.keys(sort).toString();
      if (sortKey === 'updatedAt') {
        this.clearUserPreferences(tabKey, refKey, 'sort');
        return;
      } else {
        this.userPreference[refKey][tabKey] = {
          ...this.userPreference[refKey][tabKey],
          ['sort']: sort,
        };
      }
    }
    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((data) => (this.userPreference = data)))
      .subscribe();
  }

  public async getGuidedTourConfig(): Promise<GuidedTourConfig | undefined> {
    if (!this.userPreference) {
      await this.getUserPreferences();
    }
    return this.userPreference?.['guidedTour'];
  }

  public setGuidedTourConfig(data: GuidedTourConfig) {
    if (!this.userPreference) {
      throw new Error('User preference is not defined.');
    }

    if (this.userPreference['guidedTour']) {
      this.userPreference['guidedTour'] = {
        ...this.userPreference['guidedTour'],
        ...data,
      };
    } else {
      this.userPreference['guidedTour'] = data;
    }

    this.userPreferenceApiService
      .userPreferenceControllerUpdate(this.userPreference._id, this.userPreference)
      .pipe(tap((result) => (this.userPreference = result)))
      .subscribe();
  }
}
